import { useCallback, useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import {
  buildDeductionTypeDisplayMap,
  mapDeductionTypeChoices,
} from "../components/other-liens-balance/helpers/functions";
import { useSettlementStore } from "../store/settlementStore";

import useSettlementPermissions from "./useSettlementPermissions";

export default function useOtherLiensBalanceSection() {
  const additionalExpenseRepository = useService("repositories/additional-expense-repository");
  const caseDetail = useSettlementStore((state) => state.caseDetail);
  const {
    canViewOtherLiensBalance: canViewExpenses,
    canAddOtherLiensBalance: canAddExpenses,
    canEditOtherLiensBalance: canEditExpenses,
    canDeleteOtherLiensBalance: canDeleteExpenses,
  } = useSettlementPermissions();
  const [expenses, setExpenses] = useState([]);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [deductionTypeOptions, setDeductionTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState({ mode: null, expense: null });

  const fetchExpenses = useCallback(async () => {
    if (!caseDetail?.pk || !canViewExpenses) {
      setExpenses([]);
      setExpenseTotal(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await additionalExpenseRepository.query({ case: caseDetail.pk });
      const rawData = await additionalExpenseRepository.getRawData();
      setExpenses(response || []);
      setExpenseTotal(rawData?.total_amount || 0);
    } catch (error) {
      setExpenses([]);
      setExpenseTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [additionalExpenseRepository, canViewExpenses, caseDetail?.pk]);

  const fetchDeductionTypeOptions = useCallback(async () => {
    if (!canViewExpenses) {
      setDeductionTypeOptions([]);
      return;
    }

    try {
      const response = await additionalExpenseRepository.getOptions();
      setDeductionTypeOptions(mapDeductionTypeChoices(response?.deductionTypeOptions || []));
    } catch (error) {
      setDeductionTypeOptions([]);
    }
  }, [additionalExpenseRepository, canViewExpenses]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    fetchDeductionTypeOptions();
  }, [fetchDeductionTypeOptions]);

  const deductionTypeDisplayMap = useMemo(
    () => buildDeductionTypeDisplayMap(deductionTypeOptions),
    [deductionTypeOptions]
  );

  return {
    expenses,
    expenseTotal,
    caseDetail,
    deductionTypeOptions,
    deductionTypeDisplayMap,
    isLoading,
    canViewExpenses,
    canAddExpenses,
    canEditExpenses,
    canDeleteExpenses,
    modalState,
    fetchExpenses,
    handleOpenAddModal: () => setModalState({ mode: "add", expense: null }),
    handleOpenEditModal: (expense) => setModalState({ mode: "edit", expense }),
    handleOpenDeleteModal: (expense) => setModalState({ mode: "delete", expense }),
    handleCloseModal: () => setModalState({ mode: null, expense: null }),
  };
}
